/**
 * useFileSystem - Hook per File System Access API
 * 
 * Gestisce l'accesso al filesystem locale tramite l'API File System Access
 * disponibile su Chrome, Edge e Opera.
 */

import { useState, useCallback, useEffect } from 'react';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { sanitizeFolderName } from '@/utils/sanitizeFolderName';
import { normalizeDocumentPath } from '@/utils/normalizeDocumentPath';

// Tipi estesi per File System Access API
interface FileSystemDirectoryHandleExt extends FileSystemDirectoryHandle {
  queryPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission(descriptor?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
  values(): AsyncIterableIterator<FileSystemHandle>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FileSystemDirectoryHandleExt>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
}

// Tipi per File System Access API (non presenti di default in TypeScript)
declare global {
  interface Window {
    showDirectoryPicker?: (options?: {
      id?: string;
      mode?: 'read' | 'readwrite';
      startIn?: FileSystemHandle | 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos';
    }) => Promise<FileSystemDirectoryHandleExt>;
  }
}

// Interfaccia per rappresentare un elemento nel filesystem
export interface FileSystemEntry {
  name: string;
  kind: 'file' | 'directory';
  path: string;
  handle: FileSystemHandle;
  size?: number;
  lastModified?: number;
  extension?: string;
}

// Schema IndexedDB per persistenza handle
interface FileSystemDB extends DBSchema {
  handles: {
    key: string;
    value: {
      id: string;
      handle: FileSystemDirectoryHandleExt;
      path: string;
      createdAt: number;
    };
  };
}

// Stato del filesystem
interface FileSystemState {
  isSupported: boolean;
  hasPermission: boolean;
  rootHandle: FileSystemDirectoryHandleExt | null;
  currentPath: string[];
  entries: FileSystemEntry[];
  isLoading: boolean;
  error: string | null;
}

// Database name per IndexedDB
const DB_NAME = 'studio-gori-filesystem';
const DB_VERSION = 2;

// Inizializza IndexedDB
async function getDB(): Promise<IDBPDatabase<FileSystemDB>> {
  return openDB<FileSystemDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('handles')) {
        db.createObjectStore('handles', { keyPath: 'id' });
      }
    },
  });
}

// Helper per ottenere estensione file
function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
}

// Helper per verificare supporto API
function isFileSystemAccessSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

/** Salva l'handle della cartella pratica in IndexedDB (usabile da PracticeFormPage dopo creazione cartella) */
export async function savePracticeHandle(
  practiceId: string,
  handle: FileSystemDirectoryHandle,
  path: string
): Promise<void> {
  try {
    const db = await getDB();
    await db.put('handles', {
      id: practiceId,
      handle: handle as FileSystemDirectoryHandleExt,
      path,
      createdAt: Date.now(),
    });
  } catch (error) {
    console.error('Error saving practice handle to IndexedDB:', error);
    throw error;
  }
}

/** Carica l'handle della pratica da IndexedDB solo se ha già il permesso (no prompt) */
async function loadPracticeHandle(practiceId: string): Promise<FileSystemDirectoryHandleExt | null> {
  try {
    const db = await getDB();
    const stored = await db.get('handles', practiceId);
    if (stored?.handle) {
      const permission = await stored.handle.queryPermission({ mode: 'readwrite' });
      if (permission === 'granted') return stored.handle;
    }
    return null;
  } catch {
    return null;
  }
}

/** Verifica se esiste un handle salvato in IndexedDB (senza verificare permessi) */
export async function hasSavedHandle(practiceId: string): Promise<boolean> {
  try {
    const db = await getDB();
    const stored = await db.get('handles', practiceId);
    return !!stored?.handle;
  } catch {
    return false;
  }
}

/** Richiede il permesso su un handle salvato in IndexedDB. DEVE essere chiamato in risposta a un click utente. */
export async function requestPermissionForHandle(practiceId: string): Promise<boolean> {
  try {
    const db = await getDB();
    const stored = await db.get('handles', practiceId);
    if (!stored?.handle) return false;
    const result = await stored.handle.requestPermission({ mode: 'readwrite' });
    return result === 'granted';
  } catch {
    return false;
  }
}

/**
 * Tenta di risolvere l'handle della pratica dal base path handle.
 * Se il base path è autorizzato e il documentPath indica una sotto-cartella,
 * naviga fino ad essa e salva l'handle per usi futuri.
 * Funziona senza user gesture (usa solo queryPermission, nessun prompt).
 */
export async function resolveHandleFromBasePath(
  practiceId: string,
  documentPath: string | undefined | null
): Promise<FileSystemDirectoryHandleExt | null> {
  if (!documentPath) return null;
  try {
    const db = await getDB();
    const baseStored = await db.get('handles', 'practice-base-path');
    if (!baseStored?.handle) return null;

    const baseHandle = baseStored.handle as FileSystemDirectoryHandleExt;
    const perm = await baseHandle.queryPermission({ mode: 'readwrite' });
    if (perm !== 'granted') return null;

    // Estrai il nome della cartella pratica dal documentPath
    const segments = documentPath.split(/[/\\]/).filter(Boolean);
    const folderName = segments[segments.length - 1];

    if (!folderName || folderName === baseHandle.name) {
      // documentPath punta al base path stesso
      await savePracticeHandle(practiceId, baseHandle, documentPath);
      return baseHandle;
    }

    const practiceFolder = await baseHandle.getDirectoryHandle(folderName) as FileSystemDirectoryHandleExt;
    await savePracticeHandle(practiceId, practiceFolder, documentPath);
    return practiceFolder;
  } catch {
    return null;
  }
}

/** Scrive file nella cartella pratica (root o subfolder). Restituisce i nomi scritti o null se handle non disponibile. */
export async function writeFilesToPracticeFolder(
  practiceId: string,
  files: File[],
  subfolder?: string,
  documentPath?: string
): Promise<string[] | null> {
  // Electron: usa electronFS direttamente (non servono handle IndexedDB)
  if (window.isElectron && window.electronFS && documentPath) {
    return writeFilesElectron(files, documentPath, subfolder);
  }

  let handle = await loadPracticeHandle(practiceId);
  // Fallback: prova a risolvere dal base path
  if (!handle && documentPath) {
    handle = await resolveHandleFromBasePath(practiceId, documentPath);
  }
  if (!handle) return null;
  try {
    let targetHandle = handle;
    if (subfolder) {
      const safeName = sanitizeFolderName(subfolder);
      targetHandle = await handle.getDirectoryHandle(safeName, { create: true }) as FileSystemDirectoryHandleExt;
    }
    const written: string[] = [];
    for (const file of files) {
      const fileHandle = await targetHandle.getFileHandle(file.name, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(file);
      await writable.close();
      written.push(file.name);
    }
    return written;
  } catch (error) {
    console.error('Error writing files to practice folder:', error);
    throw error;
  }
}

async function writeFilesElectron(
  files: File[],
  documentPath: string,
  subfolder?: string
): Promise<string[] | null> {
  const efs = window.electronFS!;
  const basePath = normalizeDocumentPath(documentPath) || documentPath;
  if (!efs.directoryExists(basePath)) return null;

  let targetDir = basePath;
  if (subfolder) {
    const safeName = sanitizeFolderName(subfolder);
    targetDir = efs.joinPath(basePath, safeName);
    if (!efs.directoryExists(targetDir)) {
      efs.createDirectory(targetDir);
    }
  }

  const written: string[] = [];
  for (const file of files) {
    const ab = await file.arrayBuffer();
    const filePath = efs.joinPath(targetDir, file.name);
    efs.writeFile(filePath, ab);
    written.push(file.name);
  }
  return written;
}

export function useFileSystem(practiceId: string) {
  const [state, setState] = useState<FileSystemState>({
    isSupported: isFileSystemAccessSupported(),
    hasPermission: false,
    rootHandle: null,
    currentPath: [],
    entries: [],
    isLoading: false,
    error: null,
  });

  // Salva handle in IndexedDB
  const saveHandle = useCallback(async (handle: FileSystemDirectoryHandleExt, path: string) => {
    try {
      const db = await getDB();
      await db.put('handles', {
        id: practiceId,
        handle,
        path,
        createdAt: Date.now(),
      });
    } catch (error) {
      console.error('Error saving handle to IndexedDB:', error);
    }
  }, [practiceId]);

  // Recupera handle da IndexedDB (solo se ha già il permesso, nessun prompt)
  const loadHandle = useCallback(async (): Promise<FileSystemDirectoryHandleExt | null> => {
    try {
      const db = await getDB();
      const stored = await db.get('handles', practiceId);
      if (stored?.handle) {
        const permission = await stored.handle.queryPermission({ mode: 'readwrite' });
        if (permission === 'granted') {
          return stored.handle;
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading handle from IndexedDB:', error);
      return null;
    }
  }, [practiceId]);

  // Legge il contenuto di una directory
  const readDirectory = useCallback(async (
    dirHandle: FileSystemDirectoryHandleExt,
    path: string[] = []
  ): Promise<FileSystemEntry[]> => {
    const entries: FileSystemEntry[] = [];
    
    try {
      for await (const entry of dirHandle.values()) {
        const entryPath = [...path, entry.name].join('/');
        const fsEntry: FileSystemEntry = {
          name: entry.name,
          kind: entry.kind,
          path: entryPath,
          handle: entry,
        };

        if (entry.kind === 'file') {
          const fileHandle = entry as FileSystemFileHandle;
          try {
            const file = await fileHandle.getFile();
            fsEntry.size = file.size;
            fsEntry.lastModified = file.lastModified;
            fsEntry.extension = getFileExtension(entry.name);
          } catch (e) {
            // File potrebbe non essere accessibile
          }
        }

        entries.push(fsEntry);
      }

      // Ordina: cartelle prima, poi file, alfabeticamente
      entries.sort((a, b) => {
        if (a.kind !== b.kind) {
          return a.kind === 'directory' ? -1 : 1;
        }
        return a.name.localeCompare(b.name, 'it');
      });

      return entries;
    } catch (error: any) {
      console.error('Error reading directory:', error);
      throw new Error(`Errore nella lettura della cartella: ${error.message}`);
    }
  }, []);

  // Seleziona una nuova cartella root
  // Restituisce il nome della cartella selezionata o null se annullato/errore
  const selectDirectory = useCallback(async (): Promise<string | null> => {
    if (!state.isSupported) {
      setState(prev => ({ ...prev, error: 'File System Access API non supportata in questo browser' }));
      return null;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // L'ID per showDirectoryPicker deve essere max 32 caratteri
      // Usiamo gli ultimi 24 caratteri dell'ID pratica con prefisso "p-"
      const shortId = `p-${practiceId.slice(-24)}`;
      const handle = await window.showDirectoryPicker!({
        id: shortId,
        mode: 'readwrite',
      });

      const entries = await readDirectory(handle);
      
      await saveHandle(handle, handle.name);

      setState(prev => ({
        ...prev,
        rootHandle: handle,
        hasPermission: true,
        currentPath: [],
        entries,
        isLoading: false,
        error: null,
      }));

      // Restituisci il nome della cartella
      return handle.name;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // L'utente ha annullato
        setState(prev => ({ ...prev, isLoading: false }));
        return null;
      }
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Errore nella selezione della cartella: ${error.message}`,
      }));
      return null;
    }
  }, [state.isSupported, practiceId, readDirectory, saveHandle]);

  // Naviga in una sottocartella
  const navigateTo = useCallback(async (path: string[]) => {
    if (!state.rootHandle) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let currentHandle = state.rootHandle;
      
      // Naviga fino alla cartella target
      for (const segment of path) {
        currentHandle = await currentHandle.getDirectoryHandle(segment);
      }

      const entries = await readDirectory(currentHandle, path);

      setState(prev => ({
        ...prev,
        currentPath: path,
        entries,
        isLoading: false,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Errore nella navigazione: ${error.message}`,
      }));
    }
  }, [state.rootHandle, readDirectory]);

  // Naviga nella cartella parent
  const navigateUp = useCallback(() => {
    if (state.currentPath.length === 0) return;
    navigateTo(state.currentPath.slice(0, -1));
  }, [state.currentPath, navigateTo]);

  // Naviga nella root
  const navigateToRoot = useCallback(() => {
    navigateTo([]);
  }, [navigateTo]);

  // Apri un file
  const openFile = useCallback(async (entry: FileSystemEntry): Promise<Blob | null> => {
    if (entry.kind !== 'file') return null;

    try {
      const fileHandle = entry.handle as FileSystemFileHandle;
      const file = await fileHandle.getFile();
      return file;
    } catch (error: any) {
      setState(prev => ({ ...prev, error: `Errore nell'apertura del file: ${error.message}` }));
      return null;
    }
  }, []);

  // Crea una nuova cartella
  const createFolder = useCallback(async (name: string): Promise<boolean> => {
    if (!state.rootHandle) return false;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let currentHandle = state.rootHandle;
      
      // Naviga fino alla cartella corrente
      for (const segment of state.currentPath) {
        currentHandle = await currentHandle.getDirectoryHandle(segment);
      }

      // Crea la nuova cartella
      await currentHandle.getDirectoryHandle(name, { create: true });

      // Ricarica il contenuto
      const entries = await readDirectory(currentHandle, state.currentPath);

      setState(prev => ({
        ...prev,
        entries,
        isLoading: false,
      }));

      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Errore nella creazione della cartella: ${error.message}`,
      }));
      return false;
    }
  }, [state.rootHandle, state.currentPath, readDirectory]);

  // Salva un file nella cartella corrente
  const saveFile = useCallback(async (file: File): Promise<boolean> => {
    if (!state.rootHandle) return false;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let currentHandle = state.rootHandle;
      
      // Naviga fino alla cartella corrente
      for (const segment of state.currentPath) {
        currentHandle = await currentHandle.getDirectoryHandle(segment);
      }

      // Crea/sovrascrivi il file
      const fileHandle = await currentHandle.getFileHandle(file.name, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(file);
      await writable.close();

      // Ricarica il contenuto
      const entries = await readDirectory(currentHandle, state.currentPath);

      setState(prev => ({
        ...prev,
        entries,
        isLoading: false,
      }));

      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Errore nel salvataggio del file: ${error.message}`,
      }));
      return false;
    }
  }, [state.rootHandle, state.currentPath, readDirectory]);

  // Elimina un file o cartella
  const deleteEntry = useCallback(async (entry: FileSystemEntry): Promise<boolean> => {
    if (!state.rootHandle) return false;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let currentHandle = state.rootHandle;
      
      // Naviga fino alla cartella corrente
      for (const segment of state.currentPath) {
        currentHandle = await currentHandle.getDirectoryHandle(segment);
      }

      // Elimina l'entry
      await currentHandle.removeEntry(entry.name, { recursive: entry.kind === 'directory' });

      // Ricarica il contenuto
      const entries = await readDirectory(currentHandle, state.currentPath);

      setState(prev => ({
        ...prev,
        entries,
        isLoading: false,
      }));

      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Errore nell'eliminazione: ${error.message}`,
      }));
      return false;
    }
  }, [state.rootHandle, state.currentPath, readDirectory]);

  // Ricarica la cartella corrente
  const refresh = useCallback(async () => {
    if (!state.rootHandle) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let currentHandle = state.rootHandle;
      
      for (const segment of state.currentPath) {
        currentHandle = await currentHandle.getDirectoryHandle(segment);
      }

      const entries = await readDirectory(currentHandle, state.currentPath);

      setState(prev => ({
        ...prev,
        entries,
        isLoading: false,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `Errore nel refresh: ${error.message}`,
      }));
    }
  }, [state.rootHandle, state.currentPath, readDirectory]);

  // Stato: esiste un handle salvato in IndexedDB (anche senza permesso attivo)
  const [savedHandleExists, setSavedHandleExists] = useState(false);
  // Flag: il caricamento iniziale da IndexedDB è completato
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Ricarica l'handle da IndexedDB e aggiorna lo stato (senza ricaricare la pagina)
  const reloadFromDB = useCallback(async (): Promise<boolean> => {
    const handle = await loadHandle();
    if (handle) {
      try {
        const entries = await readDirectory(handle);
        setSavedHandleExists(true);
        setState(prev => ({
          ...prev,
          rootHandle: handle,
          hasPermission: true,
          entries,
        }));
        return true;
      } catch {
        // handle non valido
      }
    }
    return false;
  }, [loadHandle, readDirectory]);

  // Richiede permesso sull'handle salvato. DEVE essere chiamato da un click utente.
  const requestPermission = useCallback(async (): Promise<boolean> => {
    const granted = await requestPermissionForHandle(practiceId);
    if (granted) {
      const handle = await loadHandle();
      if (handle) {
        try {
          const entries = await readDirectory(handle);
          setState(prev => ({
            ...prev,
            rootHandle: handle,
            hasPermission: true,
            entries,
          }));
          return true;
        } catch {
          // handle no longer valid
        }
      }
    }
    return false;
  }, [practiceId, loadHandle, readDirectory]);

  // Quando practiceId cambia, resetta lo stato e ricarica
  useEffect(() => {
    if (!state.isSupported) return;

    // Reset immediato dello stato per il nuovo practiceId
    setState(prev => ({
      ...prev,
      hasPermission: false,
      rootHandle: null,
      currentPath: [],
      entries: [],
      isLoading: false,
      error: null,
    }));
    setSavedHandleExists(false);
    setInitialLoadDone(false);

    const tryLoadHandle = async () => {
      const exists = await hasSavedHandle(practiceId);
      setSavedHandleExists(exists);

      const handle = await loadHandle();
      if (handle) {
        try {
          const entries = await readDirectory(handle);
          setState(prev => ({
            ...prev,
            rootHandle: handle,
            hasPermission: true,
            entries,
          }));
        } catch (error) {
          console.log('Stored handle no longer valid');
        }
      }
      setInitialLoadDone(true);
    };

    tryLoadHandle();
  }, [state.isSupported, practiceId, loadHandle, readDirectory]);

  // Cerca file per nome
  const searchFiles = useCallback(async (query: string): Promise<FileSystemEntry[]> => {
    if (!state.rootHandle || !query.trim()) return [];

    const results: FileSystemEntry[] = [];
    const searchQuery = query.toLowerCase();

    const searchRecursive = async (
      dirHandle: FileSystemDirectoryHandleExt,
      path: string[] = []
    ) => {
      for await (const entry of dirHandle.values()) {
        const entryPath = [...path, entry.name].join('/');
        
        if (entry.name.toLowerCase().includes(searchQuery)) {
          const fsEntry: FileSystemEntry = {
            name: entry.name,
            kind: entry.kind,
            path: entryPath,
            handle: entry,
          };

          if (entry.kind === 'file') {
            fsEntry.extension = getFileExtension(entry.name);
            try {
              const file = await (entry as FileSystemFileHandle).getFile();
              fsEntry.size = file.size;
              fsEntry.lastModified = file.lastModified;
            } catch (e) {
              // Ignore
            }
          }

          results.push(fsEntry);
        }

        // Ricerca ricorsiva nelle sottocartelle
        if (entry.kind === 'directory') {
          try {
            await searchRecursive(entry as FileSystemDirectoryHandleExt, [...path, entry.name]);
          } catch (e) {
            // Cartella non accessibile
          }
        }
      }
    };

    try {
      await searchRecursive(state.rootHandle);
    } catch (error) {
      console.error('Search error:', error);
    }

    return results;
  }, [state.rootHandle]);

  return {
    ...state,
    savedHandleExists,
    initialLoadDone,
    selectDirectory,
    navigateTo,
    navigateUp,
    navigateToRoot,
    openFile,
    createFolder,
    saveFile,
    deleteEntry,
    refresh,
    searchFiles,
    requestPermission,
    reloadFromDB,
  };
}

export default useFileSystem;
